/**
 * Les règles de l'écran « commande », sans une ligne de rendu.
 *
 * Elles vivent à part du composant pour une raison précise : l'enveloppe
 * annoncée ici doit valoir **exactement** celle que le back réservera. Une
 * règle recopiée au milieu d'un JSX ne se relit pas et ne se rejoue pas ;
 * isolée, elle se vérifie sur des cas limites.
 *
 * Aucun montant ne devient un `number` dans ce fichier : les tarifs et les
 * enveloppes sont des chaînes décimales de centièmes de token, et les convertir
 * en nombre flottant perdrait des tokens en silence au-delà de 2^53.
 */
import type { Int64 } from "../api";
import { fromBigInt, parseTokenInput, subAmounts, toBigInt } from "../format";

/**
 * Bornes reprises du contrat (`OrderMissionRequest`, `internal/adapter/in/http/dto.go`).
 *
 * Les redire ici n'est pas une duplication de la règle mais une politesse :
 * refuser une saisie manifestement hors bornes évite un aller-retour dont la
 * seule issue serait un `422`. **Le back reste l'autorité** — il revalide tout,
 * et un refus de sa part est affiché tel quel.
 */
export const JOURS_MIN = 1;
export const JOURS_MAX = 2_000;
/** Le back compte 200 **octets**, pas 200 caractères. Voir `mesurerLibelle`. */
export const LIBELLE_MAX_OCTETS = 200;

/**
 * Convertit une durée en jours-homme en l'enveloppe de tokens que la commande
 * réservera.
 *
 * C'est la règle du back, mot pour mot : `tokens = jours × tokens_per_day`.
 *
 * Il n'y a **rien à arrondir**, et c'est le principal acquis du passage au
 * jour-homme : une prestation se commande en jours entiers, donc le produit est
 * exact et l'écran ne peut pas annoncer un chiffre que la commande ne
 * confirmerait pas — sur l'écran même où le client engage son solde.
 *
 * Le calcul reste en `BigInt` de bout en bout. Le tarif est un `int64` non
 * borné du contrat : le convertir en `number` réintroduirait l'arrondi
 * silencieux au-delà de 2^53 que tout le back s'applique à éviter. La durée,
 * elle, est bornée à `JOURS_MAX`, donc sa conversion en `BigInt` est exacte.
 *
 * @param tokensParJour tarif de l'expertise, chaîne décimale du contrat.
 * @param jours durée saisie, déjà validée par `lireJours`.
 */
export function estimerEnveloppe(tokensParJour: Int64, jours: number): Int64 {
  const tarif = toBigInt(tokensParJour);
  const duree = BigInt(jours);
  if (duree <= 0n || tarif <= 0n) {
    return "0";
  }
  return fromBigInt(duree * tarif);
}

/**
 * Lit la durée saisie, ou `null` si elle n'est pas utilisable telle quelle.
 *
 * Les jours sont un nombre JSON dans le contrat, et non une chaîne : la valeur
 * est bornée à deux mille, donc très en deçà de 2^53 — la conversion est
 * exacte, et c'est le seul endroit de cet écran où elle est légitime.
 */
export function lireJours(saisie: string): number | null {
  const nettoye = saisie.replace(/\s/gu, "");
  if (!/^[0-9]+$/u.test(nettoye)) {
    return null;
  }
  const jours = Number.parseInt(nettoye, 10);
  if (jours < JOURS_MIN || jours > JOURS_MAX) {
    return null;
  }
  return jours;
}

/**
 * Diagnostic de la durée saisie, ou `null` si elle convient.
 *
 * Le texte dit ce qui est attendu, pas seulement que c'est refusé : « durée
 * invalide » n'apprend rien à qui vient de taper « une semaine ».
 */
export function erreurJours(saisie: string): string | null {
  const nettoye = saisie.replace(/\s/gu, "");
  if (nettoye === "") {
    return "Indiquez une durée, en jours-homme.";
  }
  if (!/^[0-9]+$/u.test(nettoye)) {
    return "Des jours entiers, sans virgule ni unité : 10 pour dix jours.";
  }
  const jours = Number.parseInt(nettoye, 10);
  if (jours < JOURS_MIN) {
    return "Une prestation dure au moins un jour : il n'y a pas de demi-journée.";
  }
  if (jours > JOURS_MAX) {
    return `Durée limitée à ${JOURS_MAX.toLocaleString("fr-FR")} jours.`;
  }
  return null;
}

/**
 * Longueur du libellé **en octets**, telle que le back la compte.
 *
 * Son `len(r.Label)` porte sur des octets UTF-8 : « é » en pèse deux. Compter
 * les caractères ici laisserait passer un libellé que le serveur refuserait,
 * et le compteur affiché sous le champ mentirait d'autant.
 */
export function mesurerLibelle(valeur: string): number {
  return new TextEncoder().encode(valeur).length;
}

/** Diagnostic du libellé, ou `null` s'il convient. */
export function erreurLibelle(valeur: string): string | null {
  if (valeur.trim() === "") {
    return "Un libellé est obligatoire : c'est ce que le client lira sur sa mission.";
  }
  const octets = mesurerLibelle(valeur);
  if (octets > LIBELLE_MAX_OCTETS) {
    return `Libellé limité à ${LIBELLE_MAX_OCTETS} octets (${octets} employés, accents compris).`;
  }
  return null;
}

/**
 * Ce qui manque pour couvrir l'enveloppe, ou `null` si le solde suffit.
 *
 * Rendu en chaîne décimale, comme tout montant : c'est ce chiffre qui s'affiche
 * dans « il vous manque N tokens », avant l'envoi comme après un refus.
 */
export function manqueTokens(requis: Int64, disponible: Int64): Int64 | null {
  const ecart = subAmounts(requis, disponible);
  return toBigInt(ecart) > 0n ? ecart : null;
}

/** Ce qu'il resterait de disponible une fois l'enveloppe réservée. */
export function resteApresReservation(disponible: Int64, requis: Int64): Int64 {
  return subAmounts(disponible, requis);
}

/**
 * Lit l'enveloppe saisie en tokens et la rend en centièmes, ou `null` si elle
 * n'est pas utilisable : vide, illisible, nulle, ou à plus de deux décimales.
 */
export function lireEnveloppe(saisie: string): Int64 | null {
  const centiemes = parseTokenInput(saisie.trim());
  if (centiemes === null || toBigInt(centiemes) <= 0n) {
    return null;
  }
  return centiemes;
}

/** Le message d'erreur de l'enveloppe saisie, ou `null` si elle est valable. */
export function erreurEnveloppe(saisie: string): string | null {
  if (saisie.trim() === "") {
    return "Indiquez l'enveloppe allouée au projet.";
  }
  return lireEnveloppe(saisie) === null
    ? "Un nombre de tokens strictement positif, deux décimales au plus."
    : null;
}

/**
 * Le nombre de jours entiers qu'une enveloppe couvre à un tarif : une aide à
 * la lecture, l'imputation réelle se fait au tarif de chaque affectation.
 */
export function joursCouverts(enveloppe: Int64, tokensParJour: Int64): number {
  const tarif = toBigInt(tokensParJour);
  if (tarif <= 0n) {
    return 0;
  }
  return Number(toBigInt(enveloppe) / tarif);
}
